import React, { useCallback, useEffect, useState } from 'react';
import { ClerkProvider, useClerk } from '@clerk/react';
import { getClerkStatus } from '../api/clerkAuthApi';
import AuthProvider from './AuthProvider';
import ClerkBridgeCoordinator from './ClerkBridgeCoordinator';
import { hasClerkEntrySignal } from './clerkEntryPersistence';
import ClerkServiceUnavailableScreen from './ClerkServiceUnavailableScreen';

const CLERK_STATES = new Set(['INTERNAL', 'BETA', 'ACTIVE']);
// States where Clerk is the required credential path. The backend fails legacy
// login/register closed with `403 legacy_auth_unavailable` here, so the frontend
// must never fall back to legacy credentials in these states.
const CLERK_REQUIRED_STATES = new Set(['BETA', 'ACTIVE']);
const ALL_ROLLOUT_STATES = new Set(['DISABLED', 'INTERNAL', 'BETA', 'ACTIVE']);
export const CLERK_BOOTSTRAP_MOUNT_KEY = 'mythic_clerk_bootstrap_mounts';
export const CLERK_BOOTSTRAP_LOOP_WINDOW_MS = 5000;

export const BOOTSTRAP_OUTCOMES = {
    CLERK: 'CLERK',
    LEGACY: 'LEGACY',
    UNAVAILABLE: 'UNAVAILABLE'
};

function isDevelopmentBuild() {
    return process.env.NODE_ENV === 'development';
}

export function registerBootstrapMount(now = Date.now(), storage = window.sessionStorage) {
    if (!isDevelopmentBuild()) return { blocked: false, count: 0, timestamps: [], windowMs: CLERK_BOOTSTRAP_LOOP_WINDOW_MS };

    let previous = [];
    const raw = storage.getItem(CLERK_BOOTSTRAP_MOUNT_KEY);
    if (raw) {
        try {
            previous = JSON.parse(raw);
        } catch (error) {
            throw new Error(`Invalid ${CLERK_BOOTSTRAP_MOUNT_KEY} diagnostic state: ${error.message}`);
        }
    }
    if (!Array.isArray(previous) || previous.some((timestamp) => typeof timestamp !== 'number')) {
        throw new Error(`Invalid ${CLERK_BOOTSTRAP_MOUNT_KEY} diagnostic state: expected timestamp array.`);
    }
    const timestamps = [...previous.filter((timestamp) => now - timestamp <= CLERK_BOOTSTRAP_LOOP_WINDOW_MS), now];
    storage.setItem(CLERK_BOOTSTRAP_MOUNT_KEY, JSON.stringify(timestamps));
    const blocked = timestamps.length >= 4;
    if (blocked) {
        console.error('Clerk auth loop guard tripped', {
            mountCount: timestamps.length,
            windowMs: CLERK_BOOTSTRAP_LOOP_WINDOW_MS
        });
    }
    return { blocked, count: timestamps.length, timestamps, windowMs: CLERK_BOOTSTRAP_LOOP_WINDOW_MS };
}

export function canMountClerk(status) {
    return Boolean(
        status &&
        status.configured === true &&
        status.outage !== true &&
        CLERK_STATES.has(status.state) &&
        typeof status.publishable_key === 'string' &&
        status.publishable_key.trim()
    );
}

/**
 * Resolve the rollout status into exactly one bootstrap outcome.
 *
 * | Backend state | Clerk mountable | Outcome     |
 * |---------------|-----------------|-------------|
 * | (status load failed) | n/a      | UNAVAILABLE |
 * | DISABLED      | n/a             | LEGACY      |
 * | INTERNAL      | yes             | CLERK       |
 * | INTERNAL      | no              | LEGACY      |
 * | BETA          | yes             | CLERK       |
 * | BETA          | no              | UNAVAILABLE |
 * | ACTIVE        | yes             | CLERK       |
 * | ACTIVE        | no              | UNAVAILABLE |
 *
 * `UNAVAILABLE` is terminal: it never renders legacy credentials. During
 * `BETA`/`ACTIVE` the backend rejects legacy login/register with
 * `403 legacy_auth_unavailable`, so falling through to the legacy UI would show
 * users a sign-in form that cannot succeed. When status cannot be loaded at all,
 * the frontend does not know which credential path is permitted and must not
 * guess -- inferring rollout policy in the browser is explicitly forbidden.
 *
 * Emergency rollback is intentionally absent from this matrix. Per the 2026-09-03
 * owner decision (Option A), rollback restores backend endpoints only and
 * operators use a separate known legacy entry; the status contract exposes no
 * legacy-availability field and the browser must never infer one.
 */
export function resolveBootstrapOutcome({ status, statusError }) {
    if (statusError) return BOOTSTRAP_OUTCOMES.UNAVAILABLE;
    if (!status) {
        throw new Error('resolveBootstrapOutcome requires a loaded status or a statusError.');
    }
    if (!ALL_ROLLOUT_STATES.has(status.state)) {
        throw new Error(`Unknown Clerk rollout state: ${String(status.state)}`);
    }
    if (canMountClerk(status)) return BOOTSTRAP_OUTCOMES.CLERK;
    if (CLERK_REQUIRED_STATES.has(status.state)) return BOOTSTRAP_OUTCOMES.UNAVAILABLE;
    return BOOTSTRAP_OUTCOMES.LEGACY;
}

function Loading() {
    return <div role="status">Loading authentication…</div>;
}

function ClerkAuthProvider({ children }) {
    // Capture the full Clerk instance rather than destructuring `signOut` off it.
    // The installed Clerk SDK's signOut is an instance method that reads `this`
    // internals (this.clerkjs, this.loaded, this.premountMethodCalls, etc.). Passing
    // the bare function reference down as a prop and later invoking it as
    // `clerkSignOut()` (not `clerk.signOut()`) strips its `this` binding and breaks
    // the real SDK call, even though a plain jest.fn() mock can't detect the loss.
    // An arrow-function wrapper that closes over `clerk` and calls `clerk.signOut(...)`
    // keeps the call site bound correctly without relying on Clerk's internal method
    // shape (safer than `signOut.bind(clerk)`, which would still depend on the method
    // never being reassigned/rebuilt between render and call time).
    const clerk = useClerk();
    return <AuthProvider clerkSignOut={(...args) => clerk.signOut(...args)}>{children}</AuthProvider>;
}

function ClerkStatusBootstrap({ children }) {
    const [status, setStatus] = useState(null);
    const [statusError, setStatusError] = useState(null);
    const [retryToken, setRetryToken] = useState(0);
    const [isRetrying, setIsRetrying] = useState(false);
    // Sole Clerk-entry evaluation point: persist query state synchronously so the
    // normal coordinator can resume after a Clerk session-task navigation.
    const [clerkEntry] = useState(() => hasClerkEntrySignal());
    const [loopGuard] = useState(() => registerBootstrapMount());

    useEffect(() => {
        let active = true;
        getClerkStatus()
            .then((nextStatus) => {
                if (!active) return;
                setStatus(nextStatus);
                setStatusError(null);
                setIsRetrying(false);
            })
            .catch((nextError) => {
                if (!active) return;
                setStatus(null);
                setStatusError(nextError);
                setIsRetrying(false);
            });
        return () => { active = false; };
    }, [retryToken]);

    // Retry re-fetches status in place via state, never by reloading the document.
    // A document reload would remount the bootstrap and trip the development loop
    // guard, turning a recoverable outage into a false loop diagnostic. The bridge
    // call chain is also asserted to contain no document/history navigation at all.
    const retryStatus = useCallback(() => {
        setIsRetrying(true);
        setRetryToken((token) => token + 1);
    }, []);

    if (!status && !statusError) return <Loading />;

    const outcome = resolveBootstrapOutcome({ status, statusError });

    if (outcome === BOOTSTRAP_OUTCOMES.UNAVAILABLE) {
        return <ClerkServiceUnavailableScreen onRetry={retryStatus} isRetrying={isRetrying} />;
    }

    if (outcome === BOOTSTRAP_OUTCOMES.LEGACY) {
        return <AuthProvider>{children}</AuthProvider>;
    }

    if (outcome !== BOOTSTRAP_OUTCOMES.CLERK) {
        throw new Error(`Unknown Clerk bootstrap outcome: ${String(outcome)}`);
    }

    return (
        <ClerkProvider publishableKey={status.publishable_key}>
            <ClerkAuthProvider>
                <ClerkBridgeCoordinator clerkEntry={clerkEntry} rolloutState={status.state} loopGuard={loopGuard} status={status}>
                    {children}
                </ClerkBridgeCoordinator>
            </ClerkAuthProvider>
        </ClerkProvider>
    );
}

export default ClerkStatusBootstrap;
